import { useCallback, useState } from "react";

const STORAGE_KEY = "dismissed_announcement_ids";

function read(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === "number") : [];
  } catch {
    return [];
  }
}

/** Per-user, per-announcement dismissal persisted in localStorage.
 *  Only *permanently* dismissable announcements (see
 *  ``isPermanentlyDismissable``) should be passed here; critical ones
 *  are closed in component state and re-show on reload. */
export function useDismissedAnnouncements() {
  const [ids, setIds] = useState<number[]>(read);

  const dismiss = useCallback((id: number) => {
    setIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage full / unavailable — dismissal just won't persist */
      }
      return next;
    });
  }, []);

  const restore = useCallback((id: number) => {
    setIds((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — state still updates for this session */
      }
      return next;
    });
  }, []);

  const isDismissed = useCallback((id: number) => ids.includes(id), [ids]);

  return { dismissedIds: ids, dismiss, restore, isDismissed };
}
